import { SelectOption } from '@bloomfieldrobotics/canopy';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../../app/store';
import config from '../../config';

/**
 * TODO: Replace enum usage with type
 * Environment = 'prod' | 'dev' | 'test'
 */
export enum EnvironmentEnum {
  prod = 'prod',
  dev = 'dev',
  test = 'test',
}

export const environmentOptions: SelectOption<EnvironmentEnum>[] = [
  { value: EnvironmentEnum.prod, label: 'Production' },
  { value: EnvironmentEnum.dev, label: 'Development' },
  { value: EnvironmentEnum.test, label: 'Test' },
];

/**
 * TODO: Replace enum usage with type
 * Endpoint = 'alleys' | 'blocks' | 'collections' | ...
 *
 * enum's cause more issues than they're worth with typescript. Nowadays
 * with the advancement of dev tools + find/rename references; a type and using
 * strings accomplishes the same needs and requirements
 *
 * To see some of these issues, remove the "none" option, run the app, and observe the type issues
 */
export enum EndpointEnum {
  alleys = 'alleys',
  blocks = 'blocks',
  cameras = 'cameras',
  collections = 'collections',
  customers = 'customers',
  // events = 'events',
  ranches = 'ranches',
  // roles = 'roles',
  // scanData = 'scanData',
  scans = 'scans',
  uploadSummaries = 'uploadSummaries',
  uploads = 'uploads',
  users = 'users',
  none = '',
}

export const endpointOptions: SelectOption<EndpointEnum>[] = [
  { value: EndpointEnum.alleys, label: 'Alleys' },
  { value: EndpointEnum.blocks, label: 'Blocks' },
  { value: EndpointEnum.cameras, label: 'Cameras' },
  { value: EndpointEnum.collections, label: 'Collections' },
  { value: EndpointEnum.customers, label: 'Customers' },
  // { value: EndpointEnum.events, label: 'Events' },
  { value: EndpointEnum.ranches, label: 'Ranches' },
  // { value: EndpointEnum.roles, label: 'Roles' },
  // { value: EndpointEnum.scanData, label: 'Scan Data' },
  { value: EndpointEnum.scans, label: 'Scans' },
  { value: EndpointEnum.uploadSummaries, label: 'Upload Summary' },
  { value: EndpointEnum.uploads, label: 'Uploads' },
  { value: EndpointEnum.users, label: 'Users' },
];

// Try and select the environment from the environment variable, otherwise default to prod
const defaultApiUrl =
  config.api.bloomfieldApiUrl || 'https://api.prod.bloomfield.ai';

// Extract the initial environment from the default API URL
let defaultEnvironment: EnvironmentEnum = EnvironmentEnum.prod;
if (defaultApiUrl.includes('dev')) {
  defaultEnvironment = EnvironmentEnum.dev;
} else if (defaultApiUrl.includes('test')) {
  defaultEnvironment = EnvironmentEnum.test;
}

interface NumAPIItems {
  filtered: number;
  total: number;
}

export interface APIExplorerState {
  searchValue: string;
  baseURL: string;
  fullURL: string;
  customURL: string;
  environment: EnvironmentEnum;
  endpoint: EndpointEnum;
  apiExplorerNumRowsSelected: number | null;
  numAPIItems: NumAPIItems;
}

const initialState: APIExplorerState = {
  searchValue: '',
  baseURL: defaultApiUrl,
  fullURL: defaultApiUrl + '/',
  customURL: '',
  environment: defaultEnvironment,
  endpoint: EndpointEnum.none,
  apiExplorerNumRowsSelected: null,
  numAPIItems: { filtered: 0, total: 0 },
};

export const APIExplorerStateSlice = createSlice({
  name: 'APIExplorerState',
  initialState,
  reducers: {
    initAPIExplorerState: () => initialState,
    setSearchValue: (state, action: PayloadAction<{ newValue: string }>) => {
      state.searchValue = action.payload.newValue;
    },
    setCustomURL: (state, action: PayloadAction<string>) => {
      state.customURL = action.payload;
    },
    setEnvironment: (state, action: PayloadAction<EnvironmentEnum>) => {
      const newURL = state.baseURL.replace(state.environment, action.payload);
      state.baseURL = newURL;
      state.fullURL = newURL + '/' + state.endpoint;
      state.environment = action.payload;
    },
    setEndpoint: (state, action: PayloadAction<EndpointEnum>) => {
      state.endpoint = action.payload;
      state.fullURL = state.baseURL + '/' + action.payload;
    },
    setAPIExplorerNumRowsSelected: (
      state,
      action: PayloadAction<number | null>
    ) => {
      state.apiExplorerNumRowsSelected = action.payload;
    },
    setNumAPIItems: (state, action: PayloadAction<NumAPIItems>) => {
      state.numAPIItems = action.payload;
    },
  },
});

export const {
  initAPIExplorerState,
  setSearchValue,
  setCustomURL,
  setEnvironment,
  setEndpoint,
  setAPIExplorerNumRowsSelected,
  setNumAPIItems,
} = APIExplorerStateSlice.actions;

export const selectSearchValue = (state: RootState) =>
  state.apiExplorer.searchValue;
export const selectBaseURL = (state: RootState) => state.apiExplorer.baseURL;
export const selectFullURL = (state: RootState) => state.apiExplorer.fullURL;
export const selectCustomURL = (state: RootState) =>
  state.apiExplorer.customURL;
export const selectEnvironment = (state: RootState) =>
  state.apiExplorer.environment;
export const selectEndpoint = (state: RootState) => state.apiExplorer.endpoint;
export const selectAPIExplorerNumRowsSelected = (state: RootState) =>
  state.apiExplorer.apiExplorerNumRowsSelected;
export const selectNumAPIItems = (state: RootState) =>
  state.apiExplorer.numAPIItems;
export const selectAllAPIExplorer = (state: RootState) => state.apiExplorer;

export default APIExplorerStateSlice.reducer;
